import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Loader2, ArrowLeft, RefreshCw, Trash2, ExternalLink, X, Plus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { entryService, EntryRead, AnalysisRead, TagRead } from '@/services/entry-service';

const EntryView = () => {
  const { entryId } = useParams<{ entryId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [entry, setEntry] = useState<EntryRead | null>(null);
  const [loading, setLoading] = useState(true);
  const [rerunning, setRerunning] = useState(false);
  const [deletingTag, setDeletingTag] = useState<string | null>(null);
  const [removedTags, setRemovedTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [addingTag, setAddingTag] = useState(false);
  const [tagSuggestions, setTagSuggestions] = useState<TagRead[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (entryId) {
      loadEntry();
    }
  }, [entryId]);

  useEffect(() => {
    if (newTag.length > 0) {
      loadTagSuggestions();
    } else {
      setTagSuggestions([]);
      setShowSuggestions(false);
    }
  }, [newTag]);

  const loadEntry = async () => {
    try {
      setLoading(true);
      const data = await entryService.getEntry(parseInt(entryId!));
      setEntry(data);
    } catch (error) {
      console.error('Error loading entry:', error);
      toast({
        title: 'Error loading entry',
        description: 'Failed to load the entry details',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadTagSuggestions = async () => {
    try {
      const tags = await entryService.getTags(newTag, 5);
      setTagSuggestions(tags);
      setShowSuggestions(tags.length > 0);
    } catch (error) {
      console.error('Error loading tag suggestions:', error);
    }
  };

  const handleRerunAnalysis = async () => {
    if (!entry?.id) return;
    
    try {
      setRerunning(true);
      await entryService.reAnalyzeEntry(entry.id);
      toast({
        title: 'Analysis rerun initiated',
        description: 'The analysis is being reprocessed',
      });
      // Reload entry after a delay to see updated results
      setTimeout(loadEntry, 2000);
    } catch (error) {
      console.error('Error rerunning analysis:', error);
      toast({
        title: 'Error',
        description: 'Failed to rerun analysis',
        variant: 'destructive',
      });
    } finally {
      setRerunning(false);
    }
  };

  const handleDeleteEntry = async () => {
    if (!entry?.id) return;
    
    try {
      // Toggle the deleted state: if currently deleted, restore it (false); if not deleted, delete it (true)
      const newDeletedState = !entry.deleted;
      await entryService.deleteEntry(entry.id, newDeletedState);
      toast({
        title: entry.deleted ? 'Entry restored' : 'Entry deleted',
        description: entry.deleted ? 'The entry has been restored' : 'The entry has been marked as deleted',
      });
      // Reload to update the deleted status
      loadEntry();
    } catch (error) {
      console.error('Error updating entry:', error);
      toast({
        title: 'Error',
        description: 'Failed to update entry',
        variant: 'destructive',
      });
    }
  };

  const handleAddTag = async (tagName?: string) => {
    const tagToAdd = tagName || newTag.trim();
    if (!tagToAdd || !entry) return;
    
    const latestAnalysis = entry.analyses[0];
    if (!latestAnalysis?.id) return;
    
    // Check if tag already exists
    if (latestAnalysis.tags.some(tag => tag.name === tagToAdd)) {
      toast({
        title: 'Tag already exists',
        description: `Tag "${tagToAdd}" is already added to this entry`,
        variant: 'destructive',
      });
      return;
    }
    
    try {
      setAddingTag(true);
      await entryService.addTagToAnalysis(latestAnalysis.id, tagToAdd);
      toast({
        title: 'Tag added',
        description: `Tag "${tagToAdd}" has been added`,
      });
      setNewTag('');
      setShowSuggestions(false);
      // Reload entry to get updated tags
      loadEntry();
    } catch (error) {
      console.error('Error adding tag:', error);
      toast({
        title: 'Error',
        description: 'Failed to add tag',
        variant: 'destructive',
      });
    } finally {
      setAddingTag(false);
    }
  };

  const handleRemoveTag = async (analysis: AnalysisRead, tag: TagRead) => {
    if (!analysis.id || !tag.name) return;
    
    try {
      setDeletingTag(tag.name);
      await entryService.removeTagFromAnalysis(analysis.id, tag.name);
      setRemovedTags([...removedTags, tag.name]);
      toast({
        title: 'Tag removed',
        description: `Tag "${tag.name}" has been removed`,
      });
      // Reload entry to get updated tags
      loadEntry();
    } catch (error) {
      console.error('Error removing tag:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove tag',
        variant: 'destructive',
      });
    } finally {
      setDeletingTag(null);
    }
  };

  const handleUndoRemoveTag = async () => {
    if (removedTags.length === 0 || !entry) return;
    
    const latestAnalysis = entry.analyses[0];
    if (!latestAnalysis?.id) return;
    
    const tagToRestore = removedTags[removedTags.length - 1];
    
    try {
      await entryService.addTagToAnalysis(latestAnalysis.id, tagToRestore);
      setRemovedTags(removedTags.slice(0, -1));
      toast({
        title: 'Tag restored',
        description: `Tag "${tagToRestore}" has been restored`,
      });
      loadEntry();
    } catch (error) {
      console.error('Error restoring tag:', error);
      toast({
        title: 'Error',
        description: 'Failed to restore tag',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto py-10 px-4 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="container mx-auto py-10 px-4">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Entry not found</p>
            <div className="mt-4 text-center">
              <Button onClick={() => navigate(-1)} variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const latestAnalysis = entry.analyses[0];
  const notionId = entry.external_ids?.notion?.all || entry.external_ids?.notion?.[Object.keys(entry.external_ids.notion).find(k => k !== 'all') || ''];
  const notionUrl = notionId ? `https://notion.so/${notionId.replace(/-/g, '')}` : null;
  const dropboxUrl = entry.external_ids?.file_url;

  return (
    <div className="container mx-auto py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button onClick={() => navigate(-1)} variant="outline" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Badge variant={entry.deleted ? 'destructive' : 'default'}>
            {entry.deleted ? 'Deleted' : 'Active'}
          </Badge>
        </div>

        {/* Main Card */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle>Entry #{entry.id}</CardTitle>
                <CardDescription>
                  {new Date(entry.created_at || '').toLocaleString()}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleDeleteEntry}
                  variant={entry.deleted ? 'default' : 'destructive'}
                  size="sm"
                >
                  {entry.deleted ? (
                    <>Restore</>
                  ) : (
                    <>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* External Links */}
            <div className="flex gap-2">
              {dropboxUrl && (
                <Button variant="outline" size="sm" asChild>
                  <a href={dropboxUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    View in Dropbox
                  </a>
                </Button>
              )}
              {notionUrl && (
                <Button variant="outline" size="sm" asChild>
                  <a href={notionUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    View in Notion
                  </a>
                </Button>
              )}
            </div>

            {/* Analysis Section */}
            {latestAnalysis && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Analysis</h3>
                  <Button
                    onClick={handleRerunAnalysis}
                    variant="outline"
                    size="sm"
                    disabled={rerunning}
                  >
                    {rerunning ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-2 h-4 w-4" />
                    )}
                    Re-run Analysis
                  </Button>
                </div>

                {/* Analysis Details */}
                <Card>
                  <CardContent className="pt-4 space-y-3">
                    {latestAnalysis.title && (
                      <div>
                        <h4 className="font-semibold text-sm text-muted-foreground">Title</h4>
                        <p>{latestAnalysis.title}</p>
                      </div>
                    )}
                    
                    {latestAnalysis.transcription && (
                      <div>
                        <h4 className="font-semibold text-sm text-muted-foreground">Transcription</h4>
                        <p className="whitespace-pre-wrap">{latestAnalysis.transcription}</p>
                      </div>
                    )}

                    {latestAnalysis.llm_notes && (
                      <div>
                        <h4 className="font-semibold text-sm text-muted-foreground">Notes</h4>
                        <p className="whitespace-pre-wrap">{latestAnalysis.llm_notes}</p>
                      </div>
                    )}

                    {latestAnalysis.integration && (
                      <div>
                        <h4 className="font-semibold text-sm text-muted-foreground">Integration</h4>
                        <Badge variant="secondary">{latestAnalysis.integration}</Badge>
                      </div>
                    )}

                    {/* Tags */}
                    <div>
                      <h4 className="font-semibold text-sm text-muted-foreground mb-2">Tags</h4>
                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-2">
                          {latestAnalysis.tags.map((tag) => (
                            <Badge
                              key={tag.id}
                              variant="outline"
                              className="flex items-center gap-1"
                            >
                              {tag.name}
                              <button
                                onClick={() => handleRemoveTag(latestAnalysis, tag)}
                                disabled={deletingTag === tag.name}
                                className="ml-1 hover:text-destructive"
                              >
                                {deletingTag === tag.name ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <X className="h-3 w-3" />
                                )}
                              </button>
                            </Badge>
                          ))}
                          {removedTags.length > 0 && (
                            <Button
                              onClick={handleUndoRemoveTag}
                              variant="ghost"
                              size="sm"
                            >
                              Undo
                            </Button>
                          )}
                        </div>
                        
                        {/* Add tag input */}
                        <div className="relative">
                          <div className="flex gap-2">
                            <Input
                              placeholder="Add a tag..."
                              value={newTag}
                              onChange={(e) => setNewTag(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddTag();
                                }
                              }}
                              disabled={addingTag}
                              className="h-8"
                            />
                            <Button
                              onClick={() => handleAddTag()}
                              disabled={!newTag.trim() || addingTag}
                              size="sm"
                              className="h-8"
                            >
                              {addingTag ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Plus className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                          
                          {/* Tag suggestions dropdown */}
                          {showSuggestions && tagSuggestions.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg">
                              {tagSuggestions.map((tag) => (
                                <button
                                  key={tag.id}
                                  onClick={() => {
                                    setNewTag(tag.name);
                                    handleAddTag(tag.name);
                                    setShowSuggestions(false);
                                  }}
                                  className="w-full text-left px-3 py-2 hover:bg-muted transition-colors text-sm"
                                >
                                  {tag.name}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Additional Fields */}
                    {latestAnalysis.fields && Object.keys(latestAnalysis.fields).length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm text-muted-foreground mb-3">Analysis Fields</h4>
                        {(() => {
                          const fields = latestAnalysis.fields;
                          const fieldEntries = Object.entries(fields);
                          
                          // If only one field and it's an array of objects, show it directly
                          if (fieldEntries.length === 1) {
                            const [key, value] = fieldEntries[0];
                            if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
                              const keys = Object.keys(value[0]);
                              return (
                                <div className="border rounded-lg overflow-hidden">
                                  <table className="w-full text-sm">
                                    <thead className="bg-muted/50">
                                      <tr>
                                        {keys.map(objKey => (
                                          <th key={objKey} className="text-left p-3 font-medium capitalize">
                                            {objKey.replace(/_/g, ' ')}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {value.map((item, idx) => (
                                        <tr key={idx} className={idx < value.length - 1 ? "border-b" : ""}>
                                          {keys.map(objKey => (
                                            <td key={objKey} className="p-3">
                                              <span className={
                                                typeof item[objKey] === 'boolean'
                                                  ? `inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                      item[objKey] ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                    }`
                                                  : typeof item[objKey] === 'number'
                                                    ? 'font-mono'
                                                    : ''
                                              }>
                                                {typeof item[objKey] === 'boolean' 
                                                  ? (item[objKey] ? 'Yes' : 'No')
                                                  : String(item[objKey])
                                                }
                                              </span>
                                            </td>
                                          ))}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              );
                            }
                          }
                          
                          // For multiple fields or other data types, show the regular table without Type column
                          return (
                            <div className="border rounded-lg overflow-hidden">
                              <table className="w-full text-sm">
                                <thead className="bg-muted/50">
                                  <tr>
                                    <th className="text-left p-3 font-medium">Field</th>
                                    <th className="text-left p-3 font-medium">Value</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {fieldEntries.map(([key, value], index) => {
                                    const isLast = index === fieldEntries.length - 1;
                                    
                                    // Enhanced value display logic
                                    let displayValue: string | JSX.Element;
                                    let valueClassName = '';
                                    
                                    if (Array.isArray(value)) {
                                      if (value.length === 0) {
                                        displayValue = 'Empty array';
                                        valueClassName = 'text-muted-foreground italic';
                                      } else if (typeof value[0] === 'object' && value[0] !== null) {
                                        // Array of objects - show as compact table
                                        const keys = Object.keys(value[0]);
                                        displayValue = (
                                          <div className="border rounded-lg overflow-hidden max-w-full">
                                            <table className="w-full text-xs">
                                              <thead className="bg-muted/50">
                                                <tr>
                                                  {keys.map(objKey => (
                                                    <th key={objKey} className="text-left p-2 font-medium capitalize">
                                                      {objKey.replace(/_/g, ' ')}
                                                    </th>
                                                  ))}
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {value.map((item, idx) => (
                                                  <tr key={idx} className={idx < value.length - 1 ? "border-b border-border/30" : ""}>
                                                    {keys.map(objKey => (
                                                      <td key={objKey} className="p-2">
                                                        <span className={
                                                          typeof item[objKey] === 'boolean'
                                                            ? `inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                                                item[objKey] ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                              }`
                                                            : typeof item[objKey] === 'number'
                                                              ? 'font-mono'
                                                              : ''
                                                        }>
                                                          {typeof item[objKey] === 'boolean' 
                                                            ? (item[objKey] ? 'Yes' : 'No')
                                                            : String(item[objKey])
                                                          }
                                                        </span>
                                                      </td>
                                                    ))}
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        );
                                      } else {
                                        // Array of primitives
                                        displayValue = value.join(', ');
                                      }
                                    } else if (typeof value === 'boolean') {
                                      displayValue = value ? 'Yes' : 'No';
                                      valueClassName = `inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                        value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                      }`;
                                    } else if (typeof value === 'number') {
                                      displayValue = String(value);
                                      valueClassName = 'font-mono';
                                    } else if (typeof value === 'object' && value !== null) {
                                      // Regular object - show as key-value pairs
                                      displayValue = (
                                        <div className="bg-muted/30 rounded p-2 text-xs space-y-1 max-w-md">
                                          {Object.entries(value).map(([objKey, objValue]) => (
                                            <div key={objKey} className="flex justify-between gap-2">
                                              <span className="font-medium">{objKey}:</span>
                                              <span>{String(objValue)}</span>
                                            </div>
                                          ))}
                                        </div>
                                      );
                                    } else {
                                      displayValue = String(value);
                                    }
                                    
                                    return (
                                      <tr key={key} className={!isLast ? "border-b" : ""}>
                                        <td className="p-3 font-medium capitalize">
                                          {key.replace(/_/g, ' ')}
                                        </td>
                                        <td className="p-3">
                                          <div className={valueClassName}>
                                            {displayValue}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EntryView;
